import { TextField, Typography } from "@mui/material";
import Grid from '@mui/material/Grid';
import { Autocomplete } from "@mui/material";
import throttle from 'lodash/throttle';
import LocationOnIcon from 'mdi-react/MapMarkerIcon';
import { useState, useRef, useMemo, useEffect } from 'react';
import { getPlacesApiUrl, getLocationInfo } from '../../../services/googlePlaces';
import { AutocompleteOption } from '../../../types/autocomplete';
import postalCodesToDistricts from '../../../utils/apartment/postalCodesToDistricts.json';
import { makeStyles } from "@mui/styles";
import FieldErrorTooltip from '../FieldErrorTooltip';
// Supported countries
const componentRestrictions = {
    // Finland, Åland Islands
    country: ['fi', 'ax']
};
// Autocomple types
// see https://developers.google.com/maps/documentation/javascript/reference/places-autocomplete-service
// and https://developers.google.com/places/supported_types#table3
const types = ['address'];
const loadScript = (src: string, position: HTMLElement | null, id: string) => {
    if (!position) {
        return;
    }
    const script = document.createElement('script');
    script.setAttribute('async', '');
    script.setAttribute('defer', '');
    script.setAttribute('id', id);
    script.src = src;
    position.appendChild(script);
};
const useStyles = makeStyles((theme) => ({
    displayNone: {
        display: 'none'
    },
    endAdornment: {
        right: '12px'
    },
    input: {
        height: '58px',
        fontSize: '13px',
        boxSizing: 'border-box',
        border: '1px solid rgb(232, 232, 232)',
        borderRadius: '6px',
        background: 'rgb(231, 236, 239)',
        padding: '20px !important'
    },
    inputRoot: {
        paddingRight: '0 !important',
        '&::after': {
            display: 'none'
        },
        '&::before': {
            display: 'none'
        }
    }
}));
const ReduxGoogleAutocomplete = ({ input, label, meta, isFromClientForm = false, isFromApartmentForm = false, ...custom }: any) => {
    const classes = useStyles();
    const [inputValue, setInputValue] = useState('');
    const changeFormValue = custom.changeFormValue || (() => { });
    const streetNumberFormValue = custom.streetNumberFormValue;
    const [options, setOptions] = useState<AutocompleteOption[]>(custom.existingLocations);
    const [selectedPlace, setSelectedPlace] = useState<AutocompleteOption | undefined>();
    const loaded = useRef(false);
    if (typeof window !== 'undefined' && !loaded.current) {
        if (!document.querySelector('#google-maps')) {
            loadScript(getPlacesApiUrl(), document.querySelector('head'), 'google-maps');
        }
        loaded.current = true;
    }
    const fetchPlacesInfo = useMemo(() => throttle((request: string, callback: (results: any[]) => void) => {
        if (!(window as any).google?.maps?.places?.AutocompleteService) {
            callback([]);
            return;
        }
        const service = new (window as any).google.maps.places.AutocompleteService();
        service.getPlacePredictions({
            input: request,
            types,
            componentRestrictions
        }, (results: any[] | null, status: any) => {
            const OK = (window as any).google.maps.places.PlacesServiceStatus.OK;
            callback(status === OK && results ? results : []);
        });
    }, 200), []);
    useEffect(() => {
        let active = true;
        if (inputValue === '') return;
        fetchPlacesInfo(inputValue, (results: any[]) => {
            if (active) {
                setOptions(results.map((result: any) => {
                    const description = result.description || '';
                    const parts = description.split(',')
                        .map((part: string) => part.trim())
                        .filter((part: string) => !/^(Finland|Suomi)$/i.test(part));
                    return {
                        value: result.place_id,
                        label: parts.join(', ')
                    };
                }));
            }
        });
        return () => {
            active = false;
        };
    }, [inputValue, fetchPlacesInfo]);
    const [hasFocusedToStreetNumber, setHasFocusedToStreetNumber] = useState(false);
    const applyLocationToForm = (location: any) => {
        if (!location) {
            return;
        }
        const postalCode = location.postalCode || '';
        const postalArea = location.postalArea || '';
        const googlePlaceId = location.googlePlaceId || '';
        const streetNumberMatch = location.streetAddress
            ? location.streetAddress.match(/\b\d+\b/)
            : null;
        const streetNumber = streetNumberMatch ? streetNumberMatch[0] : '';
        if (streetNumber && isFromApartmentForm) {
            changeFormValue('streetNumber', streetNumber);
        }
        if (postalCode) {
            changeFormValue('postalCode', postalCode);
        }
        if (postalArea) {
            if (isFromApartmentForm) {
                changeFormValue('postalArea', {
                    value: postalArea,
                    label: postalArea
                });
            }
            else {
                changeFormValue('postalArea', postalArea);
            }
            if (isFromApartmentForm) {
                if (postalCode && (postalCodesToDistricts as any)[postalCode]) {
                    const district = (postalCodesToDistricts as any)[postalCode];
                    changeFormValue('district', district);
                }
                else {
                    changeFormValue('district', '');
                }
            }
            if (!isFromClientForm && !isFromApartmentForm) {
                changeFormValue('googlePlaceId', googlePlaceId);
                changeFormValue('coordinates', location.coordinates);
            }
        }
    };
    useEffect(() => {
        if (selectedPlace && selectedPlace.value && selectedPlace.label) {
            const placeId = selectedPlace.value;
            const streetAddress = selectedPlace.label;
            const fetchLocation = async () => {
                const baseLocation = await getLocationInfo(placeId, streetAddress);
                applyLocationToForm(baseLocation);
                if (!baseLocation.postalCode && streetNumberFormValue) {
                    const retryLocation = await getLocationInfo(placeId, streetAddress, {
                        streetNumberOverride: streetNumberFormValue
                    });
                    applyLocationToForm({ ...baseLocation, ...retryLocation });
                }
            };
            fetchLocation().then(() => {
                if (!hasFocusedToStreetNumber) {
                    setTimeout(() => {
                        const input = document.getElementById('field-streetNumber');
                        input?.focus();
                        setHasFocusedToStreetNumber(true);
                    }, 0);
                }
            });
        }
    }, [selectedPlace, custom]);
    const { error, submitFailed } = meta;
    const showError = !!submitFailed && !!error;
    return (<div><Autocomplete id='google-map-location' value={input.value || null} getOptionLabel={(option) => typeof option === 'string' ? option : option.label.split(',')[0]} filterOptions={(x) => x} onChange={(event: any, value: any) => {
            if (value?.label) {
                value.label = value.label.split(',')[0];
                setInputValue(value.label);
                if (value?.value) {
                    setSelectedPlace(value);
                }
            }
            input.onChange(value);
            input.onBlur();
        }} classes={{
            input: classes.input,
            inputRoot: classes.inputRoot,
            endAdornment: classes.endAdornment
        }} onOpen={() => input.onBlur()} onClose={() => input.onBlur()} options={options} autoComplete includeInputInList freeSolo renderInput={(params) => (<TextField {...params} label={label} onDrop={() => input.onDrop()} onFocus={() => input.onFocus()} onDragStart={() => input.onDragStart()} onBlur={(event: any) => {
                if (typeof input?.value === 'string' &&
                    typeof event?.target?.value === 'string') {
                    // Strip trailing spaces only after input loses focus so multi-word entries remain editable.
                    const trimmedValue = event.target.value.replace(/\s+$/, '');
                    if (trimmedValue !== input.value) {
                        setInputValue(trimmedValue);
                        input.onChange(trimmedValue);
                    }
                }
                input.onBlur();
            }} fullWidth placeholder={custom.placeholder} onKeyDown={(e) => {
                if (/\d/.test(e.key) && isFromApartmentForm) {
                    e.preventDefault();
                }
            }} onChange={(event: any) => {
                let newValue = event.target.value;
                if (isFromApartmentForm) {
                    newValue = newValue.replace(/[0-9]/g, '');
                }
                setInputValue(newValue);
                input.onChange(newValue);
        }} required={custom.required || false} error={showError} helperText={undefined} autoComplete='off' variant='standard' InputProps={{
            ...params.InputProps,
            disableUnderline: true
        }}/>)}
        renderOption={(props, option) => {
            const { key, ...optionProps } = props as any;
            const optionLabel = typeof option === 'string' ? option : option.label;
            return (<li key={key} {...optionProps}>
            <Grid container alignItems='center'>
              <Grid item>
                <LocationOnIcon />
              </Grid>
              <Grid item xs>
                <Typography variant='body2'>
                  {optionLabel} {custom.selectLabel || ''}
                </Typography>
              </Grid>
            </Grid>
          </li>);
        }}/><FieldErrorTooltip error={error} visible={showError} /></div>);
};
export default ReduxGoogleAutocomplete;
